import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { TrendingUp, ArrowRight } from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

// Compact revenue snapshot for the founder — mounted on DashboardHome.
// Reads /api/founder/revenue. Renders nothing for non-founders.
function fmtEUR(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("en-IE", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(Number(n));
}

export default function FounderRevenueBrief() {
  const { user } = useAuth();
  const ownWorkspace = !user?.active_workspace || user.active_workspace === user?.id;
  const [record, setData] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const data = record?.actorId === user?.id ? record.value : null;

  useEffect(() => {
    if (!ownWorkspace || !user?.is_founder) { setData(null); return; }
    setData(null);
    setLoadState("loading");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/founder/revenue`);
        if (!data || typeof data !== "object") throw new Error();
        if (!cancelled) { setData({ actorId: user.id, value: data }); setLoadState("ready"); }
      } catch { if (!cancelled) setLoadState("error"); }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.is_founder, ownWorkspace]);

  if (!ownWorkspace || !user?.is_founder) return null;

  const transactionsAvailable = Array.isArray(data?.transactions);
  const oneTimeCount = transactionsAvailable ? data.transactions.filter((t) => t.type === "one_time").length : null;
  const oneTimeSum = !transactionsAvailable ? null : data.transactions
    .filter((t) => t.type === "one_time")
    .reduce((acc, t) => acc + Number(t.amount_eur || 0), 0);

  return (
    <section
      data-testid="founder-revenue-brief"
      className="bg-white border border-[#eee] rounded-xl p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span
            className="inline-flex items-center justify-center rounded-lg"
            style={{ background: "#EAF0FF", color: "#202B60", width: 40, height: 40 }}
          >
            <TrendingUp size={16} />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#888] font-semibold">
              Founder · revenue snapshot
            </p>
            <p className="text-[13.5px] text-[#555] mt-0.5">
              MRR from active subscriptions · one-time sales listed separately (Kickstart, domains).
            </p>
          </div>
        </div>
        <Link
          to="/dashboard/revenue"
          data-testid="founder-revenue-brief-open"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#202B60] hover:underline"
        >
          Open Revenue <ArrowRight size={13} />
        </Link>
      </div>

      {loadState !== "ready" && <p role="status" className="mt-3 text-sm text-[#666]">
        {loadState === "error" ? "Revenue figures are temporarily unavailable. Open Revenue to review." : "Loading revenue figures…"}
      </p>}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-[#F0F0F0] px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-[#888] font-semibold">MRR</p>
          <p className="text-[20px] font-bold text-[#0A1628]">{fmtEUR(data?.mrr_eur)}</p>
        </div>
        <div className="rounded-lg border border-[#F0F0F0] px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-[#888] font-semibold">ARR</p>
          <p className="text-[20px] font-bold text-[#0A1628]">{fmtEUR(data?.arr_eur)}</p>
        </div>
        <div className="rounded-lg border border-[#F0F0F0] px-4 py-3">
          <p className="text-[11px] uppercase tracking-wider text-[#888] font-semibold">One-time · lifetime</p>
          <p className="text-[20px] font-bold text-[#0A1628]">
            {fmtEUR(oneTimeSum)}
            <span className="ml-2 text-[12px] font-medium text-[#666]">· {oneTimeCount ?? "—"} tx</span>
          </p>
        </div>
      </div>
    </section>
  );
}
